/**
 * useXlsxParser Hook
 * Handles XLSX file parsing and column mapping
 */
import { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { createColumnMapping, getMissingRequiredFields } from "../utils/columnMapper";
import { normalizePhoneNumber } from "../utils/phoneNormalizer";
/**
 * Hook for parsing XLSX files and extracting user records
 */
export function useXlsxParser() {
    const [records, setRecords] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [headers, setHeaders] = useState([]);
    const [isParsing, setIsParsing] = useState(false);
    const [error, setError] = useState(null);
    const [missingRequiredFields, setMissingRequiredFields] = useState([]);
    const parseFile = useCallback(async (file) => {
        setIsParsing(true);
        setError(null);
        setRecords([]);
        setColumnMapping({});
        setHeaders([]);
        setMissingRequiredFields([]);
        try {
            // Read file as ArrayBuffer
            const arrayBuffer = await file.arrayBuffer();
            // Parse workbook for data extraction (cellStyles not needed here — we keep the raw buffer)
            const workbook = XLSX.read(arrayBuffer, { type: "array" });
            // Get first sheet
            const sheetName = workbook.SheetNames[0];
            if (!sheetName) {
                throw new Error("No sheets found in workbook");
            }
            const sheet = workbook.Sheets[sheetName];
            if (!sheet) {
                throw new Error("Could not read sheet");
            }
            // Convert to JSON with header option
            const jsonData = XLSX.utils.sheet_to_json(sheet, {
                header: 1,
                defval: "",
            });
            if (jsonData.length === 0) {
                throw new Error("No data found in sheet");
            }
            // Extract headers from first row
            const headerRow = jsonData[0];
            const cleanHeaders = headerRow.map((h) => (h ? String(h).trim() : ""));
            setHeaders(cleanHeaders);
            // Create column mapping
            const mapping = createColumnMapping(cleanHeaders);
            setColumnMapping(mapping);
            // Check for missing required fields
            const missing = getMissingRequiredFields(mapping);
            setMissingRequiredFields(missing);
            // Determine which header indices are claimed by known fields
            const claimedIndices = new Set(Object.values(mapping).filter((idx) => idx !== null));
            // Collect indices of headers that don't map to any known field (passthrough columns)
            const extraHeaderIndices = cleanHeaders
                .map((header, index) => ({ header, index }))
                .filter(({ header, index }) => header !== "" && !claimedIndices.has(index));
            // Extract user records (skip header row)
            const userRecords = [];
            for (let rowIdx = 1; rowIdx < jsonData.length; rowIdx++) {
                const row = jsonData[rowIdx];
                // Skip empty rows
                if (!row || row.every((cell) => !cell)) {
                    continue;
                }
                const record = {
                    rowNumber: rowIdx + 1, // 1-based row number (accounting for header)
                    lastName: null,
                    firstName: null,
                    middleName: null,
                    suffix: null,
                    npi: null,
                    username: null,
                    clinicalSystemId: null,
                    gender: null,
                    credentials: null,
                    specialty: null,
                    practiceName: null,
                    officeNumber: null,
                    siteNames: null,
                    email: null,
                    mobileNumber: null,
                    mobileCarrier: null,
                    pagerNumber: null,
                    pagerCarrier: null,
                    copyUserId: null,
                    action: null,
                    integrationMappings: null,
                    referenceUserAccountId: null,
                    referenceUserAccountName: null,
                    referenceUserV5Id: null,
                    createdOn: null,
                    v5UserId: null,
                    identityId: null,
                    errors: null,
                    validationErrors: [],
                    originalHeaders: cleanHeaders,
                    extraColumns: {},
                    originalFileBuffer: arrayBuffer,
                };
                // Map each field using column mapping
                for (const [fieldName, colIdx] of Object.entries(mapping)) {
                    if (colIdx === null || colIdx >= row.length) {
                        continue;
                    }
                    let value = row[colIdx];
                    // Convert to string and trim whitespace
                    if (value !== null && value !== undefined) {
                        value = String(value).trim();
                        if (value === "") {
                            value = null;
                        }
                    }
                    else {
                        value = null;
                    }
                    // Normalize phone numbers immediately during parsing
                    if (["mobileNumber", "pagerNumber", "officeNumber"].includes(fieldName) && value) {
                        value = normalizePhoneNumber(value);
                    }
                    // Set the field value
                    record[fieldName] = value;
                }
                // Capture passthrough (extra) column values
                for (const { header, index } of extraHeaderIndices) {
                    const rawValue = index < row.length ? row[index] : undefined;
                    let value = null;
                    if (rawValue !== null && rawValue !== undefined) {
                        value = String(rawValue).trim() || null;
                    }
                    record.extraColumns[header] = value;
                }
                userRecords.push(record);
            }
            setRecords(userRecords);
            // Return the parsed data directly so callers don't have to wait for state update
            return {
                records: userRecords,
                missingRequiredFields: missing,
                headers: cleanHeaders,
                columnMapping: mapping,
            };
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to parse file");
            return null;
        }
        finally {
            setIsParsing(false);
        }
    }, []);
    const clear = useCallback(() => {
        setRecords([]);
        setColumnMapping({});
        setHeaders([]);
        setError(null);
        setMissingRequiredFields([]);
    }, []);
    return {
        records,
        columnMapping,
        headers,
        isParsing,
        error,
        missingRequiredFields,
        parseFile,
        clear,
    };
}
